/*=======================================================================
 * $Id: app.js 82512 2020-06-15 17:22:07Z sabri.nuzi $
*
* Copyright © Mindbreeze Software GmbH, Linz, Austria, 2005-2020
*
* Der Nutzer des Computerprogramms anerkennt, dass der oben stehende
* Copyright-Vermerk im Sinn des Welturheberrechtsabkommens an der vom
* Urheber festgelegten Stelle in der Funktion des Computerprogramms
* angebracht bleibt, um den Vorbehalt des Urheberrechtes genuegend zum
* Ausdruck zu bringen. Dieser Urheberrechtsvermerk darf weder vom Kunden,
* Nutzer und/oder von Dritten entfernt, veraendert oder disloziert werden.
* =========================================================================*/

define(["underscore", "jquery"], function(_, $) {
  function Resizer(viewElement, viewportElement, options) {
    if (!viewElement || !viewElement.nodeType) {
      throw new Error("Resizer requires viewElement to be a valid DOM element");
    }

    if (!viewportElement || !viewportElement.nodeType) {
      throw new Error("Resizer requires viewportElement to be a valid DOM element");
    }

    this.viewElement = viewElement;
    this.viewportElement = viewportElement;
    this.options = options;
    this.lastWindowHeight = 0;
    this.lastViewportHeight = 0;
  }

  _.extend(Resizer.prototype, {
    _calcHeight: function() {
      try {
        var viewElement = this.viewElement;
        if (!viewElement) {
          return;
        }

        var viewportElement = this.viewportElement;
        var offset = this.calcOffset(viewElement, viewportElement);
        var siblingsTotalHeight = this.calcSiblingsHeight(viewElement, viewportElement);
        var viewportHeight = this.calcViewportHeight();

        var calculatedHeight = viewportHeight - offset - siblingsTotalHeight;
        var maxAvailableHeight = window.innerHeight - offset - siblingsTotalHeight;

        var effectiveHeight = maxAvailableHeight;
        // Prefer the more precise calculation if it fits
        if (calculatedHeight <= maxAvailableHeight) {
          effectiveHeight = calculatedHeight;
        }
        this.updateViewElementHeight(effectiveHeight, viewElement);
      } catch (err) {
        console.warn("Failed to calculate height!", err);
      }
    },

    calcOffset: function(elem, offsetParent) {
      if (!elem) {
        return 0;
      }
      if (offsetParent) {
        if (elem.parentElement === offsetParent) {
          return 0;
        }
      } else {
        if (!elem.parentElement) {
          return 0;
        }
      }
      try {
        var paddingMargin = this.calcPaddingAndMargin(elem);
        var sum = this.calcOffset(elem.parentElement, offsetParent) + paddingMargin;
        return sum;
      } catch (e) {
        return 0;
      }
    },

    calcPaddingAndMargin: function(elem) {
      if (!window.getComputedStyle || typeof window.getComputedStyle != "function") {
        return 0;
      }

      var computed = window.getComputedStyle(elem);
      var paddingMargin = Math.ceil(
        (this.safeParse(computed.paddingTop) || 0) +
          (this.safeParse(computed.paddingBottom) || 0) +
          (this.safeParse(computed.marginTop) || 0) +
          (this.safeParse(computed.marginBottom) || 0)
      );

      return paddingMargin;
    },

    calcSiblingsHeight: function(elem, offsetParent) {
      if (!elem || elem === offsetParent) {
        return 0;
      }
      var height = 0;
      var targetTop = elem.offsetTop;
      var sibling = elem.previousElementSibling;
      while (sibling) {
        if (sibling.offsetTop + sibling.offsetHeight <= targetTop) {
          height += this.calcPaddingAndMargin(sibling) + sibling.offsetHeight;
        }
        sibling = sibling.previousElementSibling;
      }
      var sum = height + this.calcSiblingsHeight(elem.parentElement, offsetParent);
      return sum;
    },

    calcViewportHeight: function() {
      try {
        var viewportElement = this.viewportElement;
        if (!viewportElement || !window.getComputedStyle) {
          return window.innerHeight || 0;
        }

        var viewportPaddings = this.calcPaddingAndMargin(viewportElement);
        var computedStyle = window.getComputedStyle(viewportElement);
        var borderTopAndBottom =
          this.safeParse(computedStyle.borderTopWidth) + this.safeParse(computedStyle.borderBottomWidth);
        var viewportHeight = Math.floor(this.safeParse(computedStyle.height)) - viewportPaddings - borderTopAndBottom;

        if (viewportHeight <= 0 || !isFinite(viewportHeight)) {
          return window.innerHeight || 0;
        }

        return viewportHeight;
      } catch (error) {
        console.warn("calcViewportHeight failed, falling back to window.innerHeight", error);
        return window.innerHeight || 0;
      }
    },

    resize: function() {
      var height;

      const currentWindowHeight = window.innerHeight;
      const currentViewportHeight = this.calcViewportHeight();

      // Do not trigger on x axis change
      if (currentWindowHeight === this.lastWindowHeight && currentViewportHeight === this.lastViewportHeight) {
        return;
      }

      this.lastWindowHeight = window.innerHeight;
      this.lastViewportHeight = currentViewportHeight;

      if (this.options) {
        height = this.options.height || "auto";
      }

      if (height && height !== "auto") {
        try {
          var sanitizeHeight = this.sanitizeHeightValue(height);
          this.updateViewElementHeight(sanitizeHeight, this.viewElement, this.viewportElement);
          return;
        } catch (error) {
          console.warn && console.warn(error.message, "cannot use the data-height", height);
        }
      }

      if (this.viewElement) {
        this._calcHeight();
      }
    },

    safeParse: function(value, options) {
      return this.safeParseToPx(value, options);
    },

    safeParseToPx: function(value, options) {
      const el = options && options.viewportElement;

      if (value == null) {
        throw new Error("No or Invalid value!");
      }

      if (typeof value === "number") {
        if (isFinite(value)) {
          return value;
        }
        throw new Error("Unsupported or unknown unit. Supported units (em, %, rem, vh)!");
      }

      const str = String(value).trim().toLowerCase();
      if (str === "" || str === "inherit") {
        throw new Error("Unsupported or unknown unit for data-height.");
      }

      const match = str.match(/^([-+]?[0-9]*\.?[0-9]+)([a-z%]*)$/);
      if (!match) {
        throw new Error("Unsupported or unknown unit for data-height.");
      }

      let num = parseFloat(match[1]);
      if (!isFinite(num)) {
        throw new Error("Unsupported or unknown unit for data-height.");
      }

      const unit = match[2] || "px";

      if (unit === "px") {
        return num;
      }

      // Require window for any relative unit
      if (typeof window === "undefined") {
        throw new Error("Window not available – cannot resolve relative units.");
      }

      // rem: root font size
      if (unit === "rem") {
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        if (!isFinite(rootFontSize)) {
          throw new Error("Unable to determine root font size.");
        }
        return num * rootFontSize;
      }

      // vh: viewport height
      if (unit === "vh") {
        return num * (window.innerHeight / 100);
      }

      // em: relative to viewportElement's font-size
      if (unit === "em" && el) {
        const fontSize = parseFloat(getComputedStyle(el).fontSize);
        if (!isFinite(fontSize) || fontSize === 0) {
          throw new Error("Unable to determine font-size of viewElement for 'em' unit.");
        }
        return num * fontSize;
      }

      // %: percentage of viewportElement's parent height
      // (since height % is relative to parent's height in CSS)
      if (unit === "%" && el) {
        const parentHeight = el.clientHeight;

        if (parentHeight <= 0) {
          throw new Error("Parent element has no measurable height (cannot resolve '%').");
        }

        return num * (parentHeight / 100);
      }

      if (unit === "%" || (unit === "em" && !el)) {
        console.warn(
          "viewElement is missing from parameter options.viewElement. Relative units (em, %, rem, vh) require a valid DOM element to measure font sizes."
        );
      }

      // Unsupported unit
      throw new Error("Unsupported or unknown unit for data-height.");
    },

    sanitizeHeightValue: function(newHeight) {
      if (typeof newHeight !== "string") return null;
      newHeight = newHeight.trim();

      const regex = /^([0-9]+)(px|vh|%|em|rem)$/;
      const match = regex.exec(newHeight);

      if (match && match.length === 3) {
        const number = parseInt(match[1], 10);

        const unit = match[2];
        if ((unit === "%" && number >= 0 && number <= 100) || (unit !== "%" && number >= 0)) {
          return newHeight;
        }
      }

      return null;
    },

    updateViewElementHeight: function(calculatedHeight, viewElement, viewportElement) {
      var elem = viewElement;
      elem.style.height = this.safeParse(calculatedHeight, { viewportElement }) + "px";
    }
  });

  return Resizer;
});
